import { useSelector } from 'react-redux'
import { hasFullVenueAdminAccess } from 'src/utils/adminAccess'
import { VENUE_ADMIN } from 'src/utils/roles'
import { normalizeDashboardVenueRole } from 'src/utils/venueRoleCapabilities'

/**
 * Resolve the current user's role for the active venue.
 * Priority: OMS Admin / brand admin on their own brand's venue → Primary Owner →
 * venue.additionalUsers[role] → account.venueRoles[venueId] → default VENUE_ADMIN. → default VENUE_ADMIN
 * Custom role ids (`custom_<uuid>`) are returned as stored — they must not be uppercased.
 * @returns {string} The user's role (system constant or custom role id)
 */
export const resolveCurrentDashboardUserRole = ({ account, venue } = {}) => {
  if (!account || !venue) return VENUE_ADMIN

  if (hasFullVenueAdminAccess(account, venue)) return VENUE_ADMIN

  const venueId = venue._id?.toString()

  // Primary Owner is always VENUE_ADMIN
  const venueAccountId =
    venue.account?._id || venue.account?.toString() || venue.account
  const currentAccountId = account._id || account._id?.toString()

  if (
    venueAccountId &&
    currentAccountId &&
    venueAccountId.toString() === currentAccountId.toString()
  ) {
    return VENUE_ADMIN
  }

  // Check if current user is in additionalUsers array (from backend)
  const currentUserInAdditionalUsers = venue?.additionalUsers?.find(
    u => (u._id || u._id?.toString()) === currentAccountId?.toString(),
  )

  if (currentUserInAdditionalUsers) {
    // Use the computed role field from backend, or fallback to venueRoles
    return normalizeDashboardVenueRole(
      currentUserInAdditionalUsers.role ||
        currentUserInAdditionalUsers.venueRoles?.[venueId] ||
        account?.venueRoles?.[venueId],
    )
  }

  // Fallback to account.venueRoles if available
  return normalizeDashboardVenueRole(account?.venueRoles?.[venueId])
}

const useUserRole = () => {
  const account = useSelector(state => state.auth.account)
  const venue = useSelector(state => state.venue.venue)
  return resolveCurrentDashboardUserRole({ account, venue })
}

export default useUserRole
